import { useEffect, useMemo, useState } from "react";
import { FormattedDate, FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate } from "react-router-dom";
import { endpoints } from "../api.js";
import { Icon } from "../components/Icon.jsx";
import { EmptyState, ErrorBanner, Loading, StatusPill } from "../components/ui.jsx";
import { useDocumentTitle, useErrorMessage, useLocalePath } from "../i18n/hooks.js";
import { useWorkspace } from "../workspace.jsx";

export default function Investigations() {
  useDocumentTitle("investigations.title");
  const intl = useIntl();
  const lp = useLocalePath();
  const navigate = useNavigate();
  const errMsg = useErrorMessage();
  const { search } = useWorkspace();
  const [invs, setInvs] = useState(null);
  const [error, setError] = useState(null);

  const load = () => {
    setError(null);
    endpoints.investigations().then((d) => setInvs(d.investigations || []), setError);
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    if (!invs) return null;
    const q = search.trim().toLowerCase();
    if (!q) return invs;
    return invs.filter((i) =>
      [i.name, i.target, i.targetType, i.purpose].some((v) => String(v || "").toLowerCase().includes(q)),
    );
  }, [invs, search]);

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1 className="h1">
            <FormattedMessage id="investigations.title" />
          </h1>
          {invs ? (
            <p className="muted">
              <FormattedMessage id="investigations.count" values={{ count: invs.length }} />
            </p>
          ) : null}
        </div>
        <Link className="btn btn-primary" to={lp("/investigations/new")}>
          <Icon name="plus" size={16} />
          <FormattedMessage id="nav.newInvestigation" />
        </Link>
      </header>

      <ErrorBanner message={error ? errMsg(error) : null} onRetry={load} />
      {!invs && !error ? <Loading /> : null}

      {invs && invs.length === 0 ? (
        <div className="card">
          <EmptyState
            icon="target"
            title={<FormattedMessage id="dashboard.emptyTitle" />}
            body={<FormattedMessage id="dashboard.emptyBody" />}
            action={
              <Link className="btn btn-primary" to={lp("/investigations/new")}>
                <FormattedMessage id="nav.newInvestigation" />
              </Link>
            }
          />
        </div>
      ) : null}

      {filtered && invs.length > 0 ? (
        <div className="grid-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>
                  <FormattedMessage id="investigations.col.name" />
                </th>
                <th>
                  <FormattedMessage id="investigations.col.target" />
                </th>
                <th>
                  <FormattedMessage id="investigations.col.purpose" />
                </th>
                <th>
                  <FormattedMessage id="investigations.col.tier" />
                </th>
                <th>
                  <FormattedMessage id="investigations.col.status" />
                </th>
                <th>
                  <FormattedMessage id="investigations.col.created" />
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="muted center-text">
                    <FormattedMessage id="common.noMatches" values={{ q: search }} />
                  </td>
                </tr>
              ) : null}
              {filtered.map((inv) => (
                <tr
                  key={inv.id}
                  className="is-clickable"
                  onClick={() => navigate(lp(`/investigations/${inv.id}`))}
                >
                  <td>
                    <Link to={lp(`/investigations/${inv.id}`)} className="strong-link">
                      {inv.name}
                    </Link>
                  </td>
                  <td>
                    <span className={`type-badge type-${inv.targetType}`}>
                      <FormattedMessage id={`targetType.${inv.targetType}`} defaultMessage={inv.targetType} />
                    </span>{" "}
                    <span className="mono">{inv.target}</span>
                  </td>
                  <td className="muted">
                    {intl.formatMessage({ id: `purpose.${inv.purpose}`, defaultMessage: inv.purpose })}
                  </td>
                  <td>
                    <span className="tier-tag">{inv.tier}</span>
                  </td>
                  <td>
                    <StatusPill ns="invStatus" value={inv.status} />
                  </td>
                  <td className="muted nowrap">
                    <FormattedDate value={inv.createdAt} dateStyle="medium" timeStyle="short" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
